import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  officeListPlanning,
  officeCreatePlanningEvent,
  officeDeletePlanningEvent,
  officeEstimateTravel,
  officeListEmployees,
  officeListCustomers,
  officeCheckPlanningConflicts,
  officeMovePlanningEvent,
  officeUpdateTravelBlock,
  officeExportPlanningWeek,
  officeListPlanningNotifications,
  officeExportNotificationLogsCsv,
  officeGetJobContact,
  officeCompleteJob,
  officeRecordSatisfactionPrompt,
} from "@/lib/office.functions";
import { listWaPromptTemplates } from "@/lib/whatsapp-prompt-templates.functions";
import { listWaAutoRules } from "@/lib/whatsapp-auto-rules.functions";
import {
  resolveAutoTemplateId,
  explainAutoTemplateSelection,
  DEFAULT_RULES,
  type AutoTemplateRule,
} from "@/lib/whatsapp-template-select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Plus,
  Car,
  Trash2,
  Ban,
  Wrench,
  Loader2,
  AlertTriangle,
  Download,
  RefreshCcw,
  Mail,
  MailWarning,
  MailCheck,
  CheckCircle2,
  MessageCircle,
} from "lucide-react";

export const Route = createFileRoute("/_employee/medewerker/office/planning")({
  head: () => ({
    meta: [{ title: "Planning — EVG Office" }, { name: "robots", content: "noindex, nofollow" }],
    links: [{ rel: "canonical", href: "https://elvion-group.nl/medewerker/office/planning" }],
  }),
  component: Planning,
});

type EventType = "installation" | "block" | "service" | "travel";

interface PlanningEvent {
  id: string;
  title: string;
  starts_at: string;
  ends_at: string;
  location: string | null;
  type: EventType | string;
  status: string;
  assigned_to: string | null;
  customer_id: string | null;
  notes: string | null;
  color: string | null;
  travel_minutes: number | null;
  travel_km: number | string | null;
  travel_from: string | null;
  parent_event_id: string | null;
}

// ---------- helpers ----------

function startOfWeek(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = (x.getDay() + 6) % 7; // ma=0
  x.setDate(x.getDate() - day);
  return x;
}
function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function fmtDay(d: Date): string {
  return d.toISOString().slice(0, 10);
}
function fmtDayLabel(d: Date): string {
  return d.toLocaleDateString("nl-NL", { weekday: "short", day: "numeric", month: "short" });
}
function toLocalInput(d: Date): string {
  const tz = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return tz.toISOString().slice(0, 16);
}
function typeColor(t: string, custom?: string | null): string {
  if (custom) return custom;
  if (t === "installation") return "#7c3aed"; // paars
  if (t === "service") return "#059669"; // groen
  if (t === "block") return "#dc2626"; // rood
  if (t === "travel") return "#94a3b8"; // grijs
  return "#0b2a4a";
}

const HOUR_START = 7;
const HOUR_END = 20;
const ROW_MIN = 30; // 30 min per rij
const ROW_PX = 24;

function minutesFromDayStart(iso: string, dayStart: Date): number {
  const t = new Date(iso).getTime() - dayStart.getTime();
  return Math.max(0, Math.floor(t / 60000));
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

// ---------- component ----------

function Planning() {
  const list = useServerFn(officeListPlanning);
  const create = useServerFn(officeCreatePlanningEvent);
  const remove = useServerFn(officeDeletePlanningEvent);
  const listEmp = useServerFn(officeListEmployees);
  const listCust = useServerFn(officeListCustomers);
  const qc = useQueryClient();

  const [weekStart, setWeekStart] = useState<Date>(() => startOfWeek(new Date()));
  const [employeeFilter, setEmployeeFilter] = useState<string>("all");
  const weekEnd = useMemo(() => addDays(weekStart, 7), [weekStart]);

  const eventsQ = useQuery({
    queryKey: ["office-planning", fmtDay(weekStart), employeeFilter],
    queryFn: () =>
      list({
        data: {
          from: weekStart.toISOString(),
          to: weekEnd.toISOString(),
          assigned_to: employeeFilter === "all" ? null : employeeFilter,
        },
      }),
  });
  const employeesQ = useQuery({ queryKey: ["office-employees"], queryFn: () => listEmp() });
  const customersQ = useQuery({ queryKey: ["office-customers"], queryFn: () => listCust() });

  const [dialogDay, setDialogDay] = useState<Date | null>(null);

  const events: PlanningEvent[] = (eventsQ.data?.events as PlanningEvent[]) ?? [];
  const employees = employeesQ.data?.employees ?? [];
  const customers = customersQ.data?.customers ?? [];

  const listNotifs = useServerFn(officeListPlanningNotifications);
  const eventIds = useMemo(() => events.map((e) => e.id).sort(), [events]);
  const notifsQ = useQuery({
    queryKey: ["office-planning-notifs", eventIds.join(",")],
    queryFn: () => listNotifs({ data: { event_ids: eventIds } }),
    enabled: eventIds.length > 0,
  });
  type NotifLog = {
    id: string;
    event_id: string;
    recipient_role: "customer" | "employee";
    recipient_email: string | null;
    kind: "confirmation" | "cancellation" | "update";
    status: "queued" | "skipped" | "failed";
    reason: string | null;
    created_at: string;
  };
  const notifsByEvent = useMemo(() => {
    const map: Record<string, NotifLog[]> = {};
    for (const n of ((notifsQ.data?.logs as NotifLog[]) ?? [])) {
      (map[n.event_id] ??= []).push(n);
    }
    return map;
  }, [notifsQ.data]);
  function shortReason(reason: string | null | undefined) {
    if (!reason) return "onbekende fout";
    const s = String(reason).replace(/\s+/g, " ").trim();
    return s.length > 90 ? s.slice(0, 87) + "…" : s;
  }
  function formatWhen(iso: string | null | undefined) {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" });
    } catch {
      return iso;
    }
  }
  function summarizeNotifs(list: NotifLog[] | undefined) {
    if (!list || list.length === 0) return null;
    // Neem laatste per (role) om huidige status te tonen (list is DESC gesorteerd)
    const latest = new Map<string, NotifLog>();
    for (const n of list) {
      const k = n.recipient_role;
      if (!latest.has(k)) latest.set(k, n);
    }
    const rows = Array.from(latest.values());
    const failed = rows.filter((r) => r.status === "failed");
    const queued = rows.filter((r) => r.status === "queued");
    const lastAttempt = rows.reduce<string | null>((acc, r) => {
      if (!acc) return r.created_at;
      return new Date(r.created_at) > new Date(acc) ? r.created_at : acc;
    }, null);
    return { rows, failed, queued, lastAttempt };
  }

  // Notificaties worden asynchroon geschreven (queued/failed) na een
  // mutation. We doen een korte poll (2×, 1.2s + 3s) zodat badges vanzelf
  // updaten zonder handmatige refresh.
  function refreshNotifsSoon() {
    qc.invalidateQueries({ queryKey: ["office-planning-notifs"] });
    setTimeout(() => qc.invalidateQueries({ queryKey: ["office-planning-notifs"] }), 1200);
    setTimeout(() => qc.invalidateQueries({ queryKey: ["office-planning-notifs"] }), 3000);
  }


  const days = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);
  const byDay = useMemo(() => {
    const map: Record<string, PlanningEvent[]> = {};
    for (const e of events) {
      const key = fmtDay(new Date(e.starts_at));
      (map[key] ??= []).push(e);
    }
    return map;
  }, [events]);

  const delMut = useMutation({
    mutationFn: (id: string) => remove({ data: { id } }),
    onSuccess: () => {
      toast.success("Verwijderd");
      qc.invalidateQueries({ queryKey: ["office-planning"] }); refreshNotifsSoon();
    },
    onError: (e: any) => toast.error(e.message ?? "Verwijderen mislukt"),
  });

  const move = useServerFn(officeMovePlanningEvent);
  const moveMut = useMutation({
    mutationFn: (v: { id: string; starts_at: string; ends_at: string; force?: boolean }) =>
      move({ data: { ...v, recalc_travel: true } }),
    onSuccess: () => {
      toast.success("Verplaatst — reistijd herberekend");
      qc.invalidateQueries({ queryKey: ["office-planning"] }); refreshNotifsSoon();
    },
    onError: (e: any) => {
      if (typeof e?.message === "string" && e.message.startsWith("PLANNING_CONFLICT:")) {
        if (confirm("Deze tijd overlapt met een andere afspraak. Toch verplaatsen?")) {
          const args = (window as any).__lastMove;
          if (args) moveMut.mutate({ ...args, force: true });
        }
        return;
      }
      toast.error(e.message ?? "Verplaatsen mislukt");
    },
  });

  const exportWeek = useServerFn(officeExportPlanningWeek);
  const [exporting, setExporting] = useState<"ics" | "pdf" | null>(null);
  async function doExport(kind: "ics" | "pdf") {
    setExporting(kind);
    try {
      const res = await exportWeek({
        data: {
          from: weekStart.toISOString(),
          to: weekEnd.toISOString(),
          assigned_to: employeeFilter === "all" ? null : employeeFilter,
        },
      });
      const label = `weekplanning-${fmtDay(weekStart)}`;
      if (kind === "ics") {
        const blob = new Blob([res.ics], { type: "text/calendar" });
        downloadBlob(blob, `${label}.ics`);
      } else {
        const bin = atob(res.pdfBase64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        downloadBlob(new Blob([bytes], { type: "application/pdf" }), `${label}.pdf`);
      }
      toast.success(`${res.count} afspraken geëxporteerd`);
    } catch (e: any) {
      toast.error(e.message ?? "Export mislukt");
    } finally {
      setExporting(null);
    }
  }

  const exportCsv = useServerFn(officeExportNotificationLogsCsv);
  const [exportingCsv, setExportingCsv] = useState(false);
  async function doExportCsv() {
    if (eventIds.length === 0) {
      toast.info("Geen afspraken in deze week om te exporteren");
      return;
    }
    setExportingCsv(true);
    try {
      const res = await exportCsv({ data: { event_ids: eventIds } });
      if (!res.csv) {
        toast.info("Geen verzendlogs gevonden voor deze week");
        return;
      }
      const blob = new Blob(["\uFEFF" + res.csv], { type: "text/csv;charset=utf-8" });
      downloadBlob(blob, `mail-log-${fmtDay(weekStart)}.csv`);
      toast.success(`${res.count} logregels geëxporteerd`);
    } catch (e: any) {
      toast.error(e.message ?? "CSV-export mislukt");
    } finally {
      setExportingCsv(false);
    }
  }

  const [travelFor, setTravelFor] = useState<PlanningEvent | null>(null);
  const [completeFor, setCompleteFor] = useState<PlanningEvent | null>(null);

  function handleDragStart(ev: React.DragEvent, item: PlanningEvent) {
    if (item.type !== "installation" && item.type !== "service") {
      ev.preventDefault();
      return;
    }
    ev.dataTransfer.setData("text/plain", item.id);
    ev.dataTransfer.effectAllowed = "move";
  }
  function handleCellDrop(ev: React.DragEvent, day: Date, hour: number, minute: number) {
    ev.preventDefault();
    const id = ev.dataTransfer.getData("text/plain");
    const item = events.find((e) => e.id === id);
    if (!item) return;
    const duration = new Date(item.ends_at).getTime() - new Date(item.starts_at).getTime();
    const newStart = new Date(day);
    newStart.setHours(hour, minute, 0, 0);
    const args = { id, starts_at: newStart.toISOString(), ends_at: new Date(newStart.getTime() + duration).toISOString() };
    (window as any).__lastMove = args;
    moveMut.mutate(args);
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <CalendarIcon className="size-5 text-[#0b2a4a]" />
          <h1 className="text-2xl font-bold text-slate-900">Agenda</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setWeekStart(startOfWeek(new Date()))}>
            Vandaag
          </Button>
          <Button variant="outline" size="icon" aria-label="Vorige week" onClick={() => setWeekStart(addDays(weekStart, -7))}>
            <ChevronLeft className="size-4" aria-hidden="true" />
          </Button>
          <div className="min-w-[180px] text-center text-sm font-medium text-slate-700" aria-live="polite">
            {weekStart.toLocaleDateString("nl-NL", { day: "numeric", month: "long" })} –{" "}
            {addDays(weekStart, 6).toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" })}
          </div>
          <Button variant="outline" size="icon" aria-label="Volgende week" onClick={() => setWeekStart(addDays(weekStart, 7))}>
            <ChevronRight className="size-4" aria-hidden="true" />
          </Button>
          <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Alle medewerkers" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle medewerkers</SelectItem>
              {employees.map((e: any) => (
                <SelectItem key={e.id} value={e.id}>
                  {e.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            disabled={exporting !== null}
            onClick={() => doExport("ics")}
            title="Download weekplanning als agendabestand"
          >
            {exporting === "ics" ? <Loader2 className="mr-1 size-4 animate-spin" /> : <Download className="mr-1 size-4" />}
            ICS
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={exporting !== null}
            onClick={() => doExport("pdf")}
            title="Download weekplanning als PDF"
          >
            {exporting === "pdf" ? <Loader2 className="mr-1 size-4 animate-spin" /> : <Download className="mr-1 size-4" />}
            PDF
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={exportingCsv || eventIds.length === 0}
            onClick={doExportCsv}
            title="Exporteer verzendlogs (klant + medewerker) van deze week naar CSV"
          >
            {exportingCsv ? <Loader2 className="mr-1 size-4 animate-spin" /> : <Mail className="mr-1 size-4" />}
            Mail-log CSV
          </Button>
          <Button size="sm" onClick={() => setDialogDay(new Date())}>
            <Plus className="mr-1 size-4" /> Nieuwe afspraak
          </Button>
        </div>
      </div>

      {/* Week grid */}
      <div className="overflow-x-auto rounded-2xl bg-white shadow-sm ring-1 ring-slate-200">
        <div className="grid min-w-[900px] grid-cols-[60px_repeat(7,minmax(0,1fr))]">
          {/* header */}
          <div className="border-b border-r border-slate-200 bg-slate-50" />
          {days.map((d) => {
            const isToday = fmtDay(d) === fmtDay(new Date());
            return (
              <button
                key={fmtDay(d)}
                onClick={() => setDialogDay(d)}
                className={`border-b border-r border-slate-200 px-2 py-2 text-left text-xs font-semibold hover:bg-slate-50 ${
                  isToday ? "bg-blue-50 text-[#0b2a4a]" : "text-slate-700"
                }`}
              >
                {fmtDayLabel(d)}
              </button>
            );
          })}

          {/* time rows */}
          {Array.from({ length: (HOUR_END - HOUR_START) * (60 / ROW_MIN) }).map((_, i) => {
            const totalMin = i * ROW_MIN;
            const h = HOUR_START + Math.floor(totalMin / 60);
            const m = totalMin % 60;
            const showLabel = m === 0;
            return (
              <div key={`row-${i}`} className="contents">
                <div
                  className="border-r border-slate-200 pr-1 text-right text-[10px] text-slate-400"
                  style={{ height: ROW_PX, borderBottom: m === 30 ? "1px solid #f1f5f9" : "1px solid #e2e8f0" }}
                >
                  {showLabel ? `${String(h).padStart(2, "0")}:00` : ""}
                </div>
                {days.map((d) => (
                  <div
                    key={`cell-${fmtDay(d)}-${i}`}
                    onClick={() => {
                      const day = new Date(d);
                      day.setHours(h, m, 0, 0);
                      setDialogDay(day);
                    }}
                    onDragOver={(ev) => {
                      ev.preventDefault();
                      ev.dataTransfer.dropEffect = "move";
                    }}
                    onDrop={(ev) => handleCellDrop(ev, d, h, m)}
                    className="cursor-pointer border-r border-slate-200 hover:bg-slate-50"
                    style={{ height: ROW_PX, borderBottom: m === 30 ? "1px solid #f1f5f9" : "1px solid #e2e8f0" }}
                  />
                ))}
              </div>
            );
          })}
        </div>

        {/* Event overlays per day */}
        <div className="relative -mt-[calc((var(--rows)*var(--row))+1px)] hidden" />
      </div>

      {/* Simple list view of events per day (met absolute positioning zou dubbelen zijn) */}
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
        {days.map((d) => {
          const list = byDay[fmtDay(d)] ?? [];
          if (list.length === 0) return null;
          return (
            <div key={fmtDay(d)} className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200">
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-slate-900">{fmtDayLabel(d)}</h3>
                <Button variant="ghost" size="sm" onClick={() => setDialogDay(d)}>
                  <Plus className="size-4" />
                </Button>
              </div>
              <ul className="space-y-1.5">
                {list
                  .sort((a, b) => a.starts_at.localeCompare(b.starts_at))
                  .map((e) => {
                    const draggable = e.type === "installation" || e.type === "service";
                    return (
                    <li
                      key={e.id}
                      draggable={draggable}
                      onDragStart={(ev) => handleDragStart(ev, e)}
                      className={`group flex items-start justify-between gap-2 rounded-md px-2 py-1.5 text-xs ${draggable ? "cursor-move" : ""}`}
                      style={{
                        backgroundColor: `${typeColor(e.type, e.color)}18`,
                        borderLeft: `3px solid ${typeColor(e.type, e.color)}`,
                      }}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1 font-medium text-slate-900">
                          {e.type === "block" && <Ban className="size-3" />}
                          {e.type === "travel" && <Car className="size-3" />}
                          {(e.type === "installation" || e.type === "service") && <Wrench className="size-3" />}
                          <span className="truncate">{e.title}</span>
                        </div>
                        <div className="text-slate-600">
                          {new Date(e.starts_at).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}
                          {" – "}
                          {new Date(e.ends_at).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}
                        </div>
                        {e.location && <div className="truncate text-slate-500">{e.location}</div>}
                        {e.travel_minutes && e.type !== "travel" ? (
                          <div className="mt-0.5 flex items-center gap-1 text-slate-500">
                            <Car className="size-3" /> {e.travel_minutes} min · {e.travel_km} km
                          </div>
                        ) : null}
                        {(() => {
                          const s = summarizeNotifs(notifsByEvent[e.id]);
                          if (!s) return null;
                          const when = formatWhen(s.lastAttempt);
                          if (s.failed.length > 0) {
                            const first = s.failed[0];
                            return (
                              <div
                                className="mt-0.5 flex items-start gap-1 rounded bg-red-50 px-1.5 py-1 text-[11px] text-red-700 ring-1 ring-red-200"
                                title={s.failed
                                  .map(
                                    (f) =>
                                      `${f.recipient_role} (${formatWhen(f.created_at)}): ${f.reason ?? "onbekend"}`,
                                  )
                                  .join("\n")}
                              >
                                <MailWarning className="mt-0.5 size-3 shrink-0" />
                                <span className="min-w-0 flex-1">
                                  <span className="block truncate">
                                    Mail mislukt ({first.recipient_role}): {shortReason(first.reason)}
                                  </span>
                                  {when && (
                                    <span className="block text-[10px] text-red-500/80">
                                      laatste poging {when}
                                    </span>
                                  )}
                                </span>
                              </div>
                            );
                          }
                          const anyQueued = s.queued.length > 0;
                          return (
                            <div
                              className={`mt-0.5 flex items-center gap-1 text-[11px] ${
                                anyQueued ? "text-amber-700" : "text-emerald-700"
                              }`}
                              title={s.rows
                                .map(
                                  (r) =>
                                    `${r.recipient_role} → ${r.recipient_email ?? "n.v.t."} (${r.status}) · ${formatWhen(r.created_at)}`,
                                )
                                .join("\n")}
                            >
                              <MailCheck className="size-3" />
                              <span className="truncate">
                                {anyQueued ? "Mail in wachtrij" : "Mail verstuurd"} (
                                {s.rows.map((r) => (r.recipient_role === "customer" ? "klant" : "medewerker")).join(" + ")}
                                ){when ? ` · ${when}` : ""}
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        {draggable && e.status !== "completed" && (
                          <button
                            onClick={() => setCompleteFor(e)}
                            className="opacity-0 transition group-hover:opacity-100"
                            title="Klus afronden + review-uitnodiging"
                          >
                            <CheckCircle2 className="size-3.5 text-slate-400 hover:text-emerald-600" />
                          </button>
                        )}
                        {e.status === "completed" && (
                          <span title="Afgerond" className="inline-flex">
                            <CheckCircle2 className="size-3.5 text-emerald-600" />
                          </span>
                        )}
                        {draggable && (
                          <button
                            onClick={() => setTravelFor(e)}
                            className="opacity-0 transition group-hover:opacity-100"
                            title="Reistijd aanpassen"
                          >
                            <Car className="size-3.5 text-slate-400 hover:text-[#0b2a4a]" />
                          </button>
                        )}
                        <button
                          onClick={() => delMut.mutate(e.id)}
                          className="opacity-0 transition group-hover:opacity-100"
                          title="Verwijderen"
                        >
                          <Trash2 className="size-3.5 text-slate-400 hover:text-red-500" />
                        </button>
                      </div>
                    </li>
                    );
                  })}
              </ul>
            </div>
          );
        })}
      </div>

      {dialogDay && (
        <EventDialog
          day={dialogDay}
          employees={employees}
          customers={customers}
          onClose={() => setDialogDay(null)}
          onCreate={async (payload) => {
            await create({ data: payload });
            qc.invalidateQueries({ queryKey: ["office-planning"] }); refreshNotifsSoon();
          }}
        />
      )}

      {travelFor && (
        <TravelDialog
          event={travelFor}
          onClose={() => setTravelFor(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ["office-planning"] }); refreshNotifsSoon(); }}
        />
      )}

      {completeFor && (
        <CompleteJobDialog
          event={completeFor}
          onClose={() => setCompleteFor(null)}
          onDone={() => {
            setCompleteFor(null);
            qc.invalidateQueries({ queryKey: ["office-planning"] });
          }}
        />
      )}
    </div>
  );
}

// ---------- dialog ----------

const HQ_DEFAULT = "Elvion Group Nederland, Utrecht"; // wordt als vertrekpunt gebruikt als geen ander adres bekend is

function EventDialog(props: {
  day: Date;
  employees: Array<{ id: string; name: string; email: string | null }>;
  customers: Array<{ id: string; full_name: string | null; email: string | null; phone: string | null }>;
  onClose: () => void;
  onCreate: (payload: any) => Promise<void>;
}) {
  const est = useServerFn(officeEstimateTravel);
  const initialStart = new Date(props.day);
  if (initialStart.getHours() < HOUR_START) initialStart.setHours(9, 0, 0, 0);
  const initialEnd = new Date(initialStart.getTime() + 90 * 60000);

  const [type, setType] = useState<EventType>("installation");
  const [title, setTitle] = useState("");
  const [starts, setStarts] = useState(toLocalInput(initialStart));
  const [ends, setEnds] = useState(toLocalInput(initialEnd));
  const [customerId, setCustomerId] = useState<string>("none");
  const [employeeId, setEmployeeId] = useState<string>("none");
  const [location, setLocation] = useState("");
  const [notes, setNotes] = useState("");
  const [travelFrom, setTravelFrom] = useState(HQ_DEFAULT);
  const [autoTravel, setAutoTravel] = useState(true);
  const [travelMin, setTravelMin] = useState<number | null>(null);
  const [travelKm, setTravelKm] = useState<number | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [saving, setSaving] = useState(false);

  const canEstimate = autoTravel && travelFrom.trim().length > 2 && location.trim().length > 2;

  async function runEstimate() {
    if (!canEstimate) return;
    setEstimating(true);
    try {
      const r = await est({ data: { from: travelFrom, to: location } });
      if (r.ok) {
        setTravelMin(r.minutes ?? null);
        setTravelKm(r.km ?? null);
        toast.success(`Reistijd: ${r.minutes} min · ${r.km} km`);
      } else {
        toast.error("Kon reistijd niet berekenen — vul handmatig in");
      }
    } catch (e: any) {
      toast.error(e.message ?? "Fout bij reistijd");
    } finally {
      setEstimating(false);
    }
  }

  type Conflict = { id: string; title: string; starts_at: string; ends_at: string; type: string; scope: "employee" | "customer" };
  const [conflicts, setConflicts] = useState<Conflict[]>([]);
  const check = useServerFn(officeCheckPlanningConflicts);

  function buildPayload(force: boolean) {
    return {
      title: title.trim(),
      starts_at: new Date(starts).toISOString(),
      ends_at: new Date(ends).toISOString(),
      type,
      location: location.trim() || null,
      notes: notes.trim() || null,
      assigned_to: employeeId === "none" ? null : employeeId,
      customer_id: customerId === "none" ? null : customerId,
      color: type === "block" ? "#dc2626" : null,
      auto_travel: type === "installation" || type === "service" ? autoTravel : false,
      travel_from: autoTravel && location ? travelFrom : null,
      travel_minutes: travelMin,
      travel_km: travelKm,
      force,
    };
  }

  async function submit(force = false) {
    if (!title.trim()) {
      toast.error("Titel is verplicht");
      return;
    }
    if (new Date(ends) <= new Date(starts)) {
      toast.error("Einde moet na de start liggen");
      return;
    }
    setConflicts([]);
    setSaving(true);
    try {
      // Pre-check: waarschuw voordat we insert doen
      if (!force) {
        const r = await check({
          data: {
            starts_at: new Date(starts).toISOString(),
            ends_at: new Date(ends).toISOString(),
            assigned_to: employeeId === "none" ? null : employeeId,
            customer_id: customerId === "none" ? null : customerId,
            travel_minutes: travelMin,
            auto_travel: type === "installation" || type === "service" ? autoTravel : false,
          },
        });
        if (r.conflicts && r.conflicts.length > 0) {
          setConflicts(r.conflicts as Conflict[]);
          setSaving(false);
          return;
        }
      }
      await props.onCreate(buildPayload(force));
      toast.success("Ingepland");
      props.onClose();
    } catch (e: any) {
      const msg: string = e?.message ?? "Opslaan mislukt";
      // Server-side fallback: als er tussen check en insert alsnog een overlap ontstond
      if (msg.startsWith("PLANNING_CONFLICT:")) {
        try {
          const parsed = JSON.parse(msg.slice("PLANNING_CONFLICT:".length));
          setConflicts(parsed as Conflict[]);
        } catch {
          toast.error("Er is een dubbele boeking gedetecteerd");
        }
      } else {
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  }

  const showTravel = type === "installation" || type === "service";

  return (
    <Dialog open onOpenChange={(o) => !o && props.onClose()}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Afspraak op{" "}
            {props.day.toLocaleDateString("nl-NL", {
              weekday: "long",
              day: "numeric",
              month: "long",
            })}
          </DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          <div className="grid grid-cols-4 gap-2">
            {(
              [
                { v: "installation", l: "Installatie", i: Wrench, c: "#7c3aed" },
                { v: "service", l: "Service", i: Wrench, c: "#059669" },
                { v: "block", l: "Blokkeren", i: Ban, c: "#dc2626" },
                { v: "travel", l: "Reistijd", i: Car, c: "#94a3b8" },
              ] as const
            ).map((o) => {
              const Icon = o.i;
              const active = type === o.v;
              return (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setType(o.v)}
                  className={`flex flex-col items-center gap-1 rounded-lg border-2 p-2 text-xs transition ${
                    active ? "border-[#0b2a4a] bg-slate-50" : "border-slate-200 hover:border-slate-300"
                  }`}
                  style={active ? { borderColor: o.c } : undefined}
                >
                  <Icon className="size-4" style={{ color: o.c }} />
                  {o.l}
                </button>
              );
            })}
          </div>

          <div className="grid gap-1.5">
            <Label>Titel</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Bijv. Installatie camerabewaking" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Start</Label>
              <Input type="datetime-local" value={starts} onChange={(e) => setStarts(e.target.value)} />
            </div>
            <div className="grid gap-1.5">
              <Label>Einde</Label>
              <Input type="datetime-local" value={ends} onChange={(e) => setEnds(e.target.value)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label>Medewerker</Label>
              <Select value={employeeId} onValueChange={setEmployeeId}>
                <SelectTrigger>
                  <SelectValue placeholder="Kies medewerker" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Niemand —</SelectItem>
                  {props.employees.map((e) => (
                    <SelectItem key={e.id} value={e.id}>
                      {e.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {type !== "block" && (
              <div className="grid gap-1.5">
                <Label>Klant</Label>
                <Select value={customerId} onValueChange={setCustomerId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Kies klant" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="none">— Geen klant —</SelectItem>
                    {props.customers.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.full_name || c.email || c.id.slice(0, 8)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {showTravel && (
            <div className="grid gap-1.5">
              <Label>Locatie / adres</Label>
              <Input
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Straat 12, 3511 AB Utrecht"
              />
            </div>
          )}

          {showTravel && (
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium text-slate-800">
                  <Car className="size-4" /> Reistijd automatisch inplannen
                </div>
                <label className="flex items-center gap-2 text-xs">
                  <input
                    type="checkbox"
                    checked={autoTravel}
                    onChange={(e) => setAutoTravel(e.target.checked)}
                  />
                  Aan
                </label>
              </div>
              {autoTravel && (
                <div className="space-y-2">
                  <div className="grid gap-1.5">
                    <Label className="text-xs">Vertrek vanaf</Label>
                    <Input
                      value={travelFrom}
                      onChange={(e) => setTravelFrom(e.target.value)}
                      placeholder="Adres of plaats"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={!canEstimate || estimating}
                      onClick={runEstimate}
                    >
                      {estimating ? <Loader2 className="mr-1 size-3 animate-spin" /> : <Car className="mr-1 size-3" />}
                      Bereken reistijd
                    </Button>
                    {travelMin != null && (
                      <div className="text-xs text-slate-700">
                        <b>{travelMin}</b> min · <b>{travelKm}</b> km — wordt automatisch als blok vóór de afspraak
                        gereserveerd.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="grid gap-1.5">
            <Label>Notities</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>

          {conflicts.length > 0 && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm">
              <div className="mb-2 flex items-center gap-2 font-semibold text-amber-900">
                <AlertTriangle className="size-4" />
                Overlappende tijdblokken gevonden
              </div>
              <ul className="mb-2 space-y-1 text-amber-900">
                {conflicts.map((c) => (
                  <li key={`${c.scope}-${c.id}`} className="flex items-start gap-2">
                    <span className="mt-0.5 rounded bg-amber-200 px-1.5 py-0.5 text-[10px] uppercase">
                      {c.scope === "employee" ? "Medewerker" : "Klant"}
                    </span>
                    <span>
                      <b>{c.title}</b> ·{" "}
                      {new Date(c.starts_at).toLocaleString("nl-NL", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                      {" – "}
                      {new Date(c.ends_at).toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="text-xs text-amber-800">
                Kies een ander tijdstip/medewerker, of plan toch in als deze overlap acceptabel is.
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={props.onClose} disabled={saving}>
            Annuleren
          </Button>
          {conflicts.length > 0 && (
            <Button variant="destructive" onClick={() => submit(true)} disabled={saving}>
              {saving ? <Loader2 className="mr-1 size-4 animate-spin" /> : <AlertTriangle className="mr-1 size-4" />}
              Toch inplannen
            </Button>
          )}
          <Button onClick={() => submit(false)} disabled={saving}>
            {saving ? <Loader2 className="mr-1 size-4 animate-spin" /> : <Plus className="mr-1 size-4" />}
            Inplannen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- travel dialog ----------

function TravelDialog(props: {
  event: PlanningEvent;
  onClose: () => void;
  onSaved: () => void;
}) {
  const update = useServerFn(officeUpdateTravelBlock);
  const [from, setFrom] = useState<string>(props.event.travel_from ?? HQ_DEFAULT);
  const [minutes, setMinutes] = useState<number>(props.event.travel_minutes ?? 0);
  const [km, setKm] = useState<number>(Number(props.event.travel_km ?? 0));
  const [busy, setBusy] = useState<"save" | "recalc" | null>(null);
  const [pendingConflicts, setPendingConflicts] = useState<
    { conflicts: Array<{ id: string; title: string; starts_at: string; ends_at: string; scope: string }>; recalc: boolean; minutes: number; km: number } | null
  >(null);

  async function run(recalc: boolean, force = false) {
    setBusy(recalc ? "recalc" : "save");
    try {
      const r: any = await update({
        data: {
          id: props.event.id,
          travel_from: from,
          travel_minutes: minutes,
          travel_km: km,
          recalc,
          force,
        },
      });
      if (r && r.ok === false && r.conflict) {
        setPendingConflicts({ conflicts: r.conflicts ?? [], recalc, minutes: r.minutes ?? minutes, km: Number(r.km ?? km) });
        toast.warning("Reistijdblok veroorzaakt overlap — bevestig om door te gaan");
        return;
      }
      if (recalc) {
        setMinutes(r.minutes ?? minutes);
        setKm(Number(r.km ?? km));
        toast.success(`Reistijd herberekend: ${r.minutes} min · ${r.km ?? "?"} km`);
      } else {
        toast.success("Reistijdblok bijgewerkt");
        props.onSaved();
        props.onClose();
      }
      setPendingConflicts(null);
      props.onSaved();
    } catch (e: any) {
      toast.error(e.message ?? "Bijwerken mislukt");
    } finally {
      setBusy(null);
    }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && props.onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Car className="size-5" /> Reistijd — {props.event.title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-xs text-slate-500">
            Pas het gekoppelde reistijdblok aan. De installatie- of service-afspraak blijft ongewijzigd.
          </p>
          <div>
            <Label>Vertrekadres</Label>
            <Input value={from} onChange={(e) => setFrom(e.target.value)} placeholder="Straat 1, Plaats" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Minuten</Label>
              <Input
                type="number"
                min={0}
                value={minutes}
                onChange={(e) => setMinutes(Number(e.target.value) || 0)}
              />
            </div>
            <div>
              <Label>Kilometers</Label>
              <Input
                type="number"
                min={0}
                step={0.1}
                value={km}
                onChange={(e) => setKm(Number(e.target.value) || 0)}
              />
            </div>
          </div>
          {pendingConflicts && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
              <div className="mb-1 flex items-center gap-1 text-xs font-semibold text-amber-900">
                <AlertTriangle className="size-4" /> Overlap gedetecteerd
              </div>
              <p className="mb-2 text-[11px] text-amber-800">
                Het nieuwe reistijdvenster overlapt met {pendingConflicts.conflicts.length} andere afspraak
                {pendingConflicts.conflicts.length === 1 ? "" : "en"}. Controleer voor je definitief opslaat.
              </p>
              <ul className="space-y-1 text-[11px] text-amber-900">
                {pendingConflicts.conflicts.slice(0, 5).map((c) => (
                  <li key={c.id} className="flex justify-between gap-2">
                    <span className="truncate">{c.title}</span>
                    <span className="tabular-nums">
                      {new Date(c.starts_at).toLocaleString("nl-NL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={props.onClose} disabled={busy !== null}>
            Annuleren
          </Button>
          {pendingConflicts ? (
            <>
              <Button variant="ghost" onClick={() => setPendingConflicts(null)} disabled={busy !== null}>
                Terug
              </Button>
              <Button variant="destructive" onClick={() => run(pendingConflicts.recalc, true)} disabled={busy !== null}>
                {busy !== null ? <Loader2 className="mr-1 size-4 animate-spin" /> : <AlertTriangle className="mr-1 size-4" />}
                Toch opslaan
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={() => run(true)} disabled={busy !== null}>
                {busy === "recalc" ? (
                  <Loader2 className="mr-1 size-4 animate-spin" />
                ) : (
                  <RefreshCcw className="mr-1 size-4" />
                )}
                Herbereken
              </Button>
              <Button onClick={() => run(false)} disabled={busy !== null}>
                {busy === "save" ? <Loader2 className="mr-1 size-4 animate-spin" /> : null}
                Opslaan
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Klus afronden dialog ----------

function CompleteJobDialog(props: {
  event: PlanningEvent;
  onClose: () => void;
  onDone: () => void;
}) {
  const getContact = useServerFn(officeGetJobContact);
  const complete = useServerFn(officeCompleteJob);
  const recordPrompt = useServerFn(officeRecordSatisfactionPrompt);

  const contactQ = useQuery({
    queryKey: ["job-contact", props.event.id],
    queryFn: () => getContact({ data: { eventId: props.event.id } }),
  });


  const [satisfied, setSatisfied] = useState<"yes" | "no" | "unknown">("unknown");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const c = contactQ.data;
  const phoneDigits = (c?.phone ?? "").replace(/[^\d+]/g, "").replace(/^\+/, "");

  // Persoonlijke variabelen per klus
  const firstName = c?.name ? c.name.split(" ")[0] : "";
  const greeting = firstName ? `Hallo ${firstName}` : "Hallo";
  const dateLabel = c?.startsAt
    ? new Date(c.startsAt).toLocaleDateString("nl-NL", { weekday: "long", day: "numeric", month: "long" })
    : "";
  const locationLabel = c?.location?.trim() || "";
  const orderPart = c?.orderNumber ? ` voor bestelling ${c.orderNumber}` : "";
  const whenPart = dateLabel ? ` van ${dateLabel}` : "";
  const wherePart = locationLabel ? ` op ${locationLabel}` : "";

  // Templates uit de database (beheerd door admin op /admin/whatsapp-templates).
  // Fallback naar hardgecodeerde defaults als de DB (nog) leeg is of het endpoint faalt.
  const listTemplates = useServerFn(listWaPromptTemplates);
  const tplQ = useQuery({
    queryKey: ["wa-prompt-templates-active"],
    queryFn: () => listTemplates({ data: { includeArchived: false } }),
    staleTime: 60_000,
  });

  // Beheerbare auto-selectieregels (per klanttype / installatietype). Zonder
  // data valt de helper terug op DEFAULT_RULES zodat de UI nooit blokkeert.
  const listAutoRules = useServerFn(listWaAutoRules);
  const autoRulesQ = useQuery({
    queryKey: ["wa-auto-rules-active"],
    queryFn: () => listAutoRules({ data: { includeInactive: false } }),
    staleTime: 60_000,
  });
  const autoRules: AutoTemplateRule[] = useMemo(() => {
    const rows = (autoRulesQ.data?.items ?? []) as any[];
    if (rows.length === 0) return DEFAULT_RULES;
    return rows.map((r) => ({
      sortOrder: r.sort_order,
      matchCustomerType: r.match_customer_type ?? null,
      keywordPattern: r.keyword_pattern ?? null,
      templateSlug: r.template_slug,
      description: r.description,
    }));
  }, [autoRulesQ.data]);

  const vars: Record<string, string> = {
    "{greeting}": greeting,
    "{orderPart}": orderPart,
    "{wherePart}": wherePart,
    "{whenPart}": whenPart,
    "{firstName}": firstName,
    "{orderNumber}": c?.orderNumber ?? "",
    "{location}": locationLabel,
    "{date}": dateLabel,
  };
  const renderBody = (body: string): string => {
    let out = body;
    for (const [k, v] of Object.entries(vars)) out = out.split(k).join(v);
    return out;
  };

  const FALLBACK_TEMPLATES = useMemo(
    () => [
      { id: "standaard", label: "Standaard (consument)", text: `${greeting}, dit is Elvion Group Nederland. We hebben de werkzaamheden${orderPart}${wherePart}${whenPart} zojuist afgerond. Bent u tevreden met het geleverde werk? Wij horen het graag. Bedankt!` },
      { id: "zakelijk", label: "Zakelijk / B2B", text: `${greeting}, namens Elvion Group Nederland: de installatie${orderPart}${wherePart}${whenPart} is opgeleverd. Wij horen graag of alles naar wens is verlopen. Alvast bedankt voor de samenwerking!` },
      { id: "camera", label: "Camerabewaking", text: `${greeting}, uw camerasysteem${orderPart} is${wherePart}${whenPart} geïnstalleerd en getest. Kunt u meekijken via de app en laten weten of het beeld naar wens is? Alvast bedankt!` },
      { id: "groepenkast", label: "Groepenkast / elektra", text: `${greeting}, de werkzaamheden aan uw groepenkast${orderPart}${wherePart}${whenPart} zijn afgerond en getest volgens NEN 1010. Bent u tevreden met de installatie? Wij horen het graag. Bedankt!` },
      { id: "laadpaal", label: "Laadpaal", text: `${greeting}, uw laadpaal${orderPart} is${wherePart}${whenPart} geïnstalleerd en succesvol getest. Laat u ons weten of het opladen naar wens verloopt? Alvast bedankt!` },
      { id: "service", label: "Service / storing", text: `${greeting}, de servicemelding${orderPart} is${wherePart}${whenPart} opgelost. Werkt alles weer naar behoren? Fijn als u dat kort bevestigt. Alvast bedankt!` },
    ],
    [greeting, orderPart, wherePart, whenPart],
  );

  type TplItem = { id: string; label: string; text: string };
  // Als de query succesvol was maar er staan géén (actieve) sjablonen in de DB,
  // dan is de app expliciet niet-geconfigureerd. We willen dan géén stille
  // fallback op hard-coded teksten, maar een duidelijke foutmelding + geblok-
  // keerde verzendknop. Alleen bij een echte laadfout (netwerkstoring) vallen
  // we terug op FALLBACK_TEMPLATES zodat je toch verder kunt werken.
  const noTemplatesConfigured =
    tplQ.isSuccess && ((tplQ.data?.items ?? []) as unknown[]).length === 0;
  const templates: TplItem[] = useMemo(() => {
    const items = (tplQ.data?.items ?? []) as Array<{ slug: string; label: string; body: string }>;
    if (items.length === 0) {
      // Bij een expliciete "niets geconfigureerd" geven we een lege lijst
      // terug zodat de UI de foutmelding kan tonen en verzenden blokkeert.
      if (tplQ.isSuccess) return [];
      // Netwerkfout / nog aan het laden → veilig terugvallen.
      return FALLBACK_TEMPLATES;
    }
    return items.map((t) => ({ id: t.slug, label: t.label, text: renderBody(t.body) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tplQ.data, tplQ.isSuccess, greeting, orderPart, wherePart, whenPart, firstName, c?.orderNumber, locationLabel, dateLabel, FALLBACK_TEMPLATES]);

  // Automatische keuze via de gedeelde helper (unit-getest in
  // scripts/test-whatsapp-template-select.test.ts). Zorgt dat er altijd een
  // beschikbare slug wordt gekozen — ook zonder keyword-match of als admins
  // sjablonen hebben gearchiveerd.
  const autoTemplateId = useMemo(
    () =>
      resolveAutoTemplateId(
        {
          jobTitle: c?.jobTitle,
          eventType: (c as any)?.eventType,
          location: c?.location,
          customerType: (c as any)?.customerType,
        },
        templates.map((t) => t.id),
        autoRules,
      ),
    [c?.jobTitle, (c as any)?.eventType, c?.location, (c as any)?.customerType, templates, autoRules],
  );

  // Uitleg waarom de auto-selectie op dit sjabloon uitkwam — getoond als
  // tooltip + tekstlabel onder de sjabloonchips.
  const autoReason = useMemo(
    () =>
      explainAutoTemplateSelection(
        {
          jobTitle: c?.jobTitle,
          eventType: (c as any)?.eventType,
          location: c?.location,
          customerType: (c as any)?.customerType,
        },
        autoRules,
      ),
    [c?.jobTitle, (c as any)?.eventType, c?.location, (c as any)?.customerType, autoRules],
  );
  // Als de gekozen auto-slug niet met de wens-slug matcht (bv. wish bestond niet),
  // reflecteer dat in de uitleg zodat de medewerker weet dat er is teruggevallen.
  const autoReasonText = useMemo(() => {
    if (autoReason.slug && autoReason.slug === autoTemplateId) {
      return autoReason.reason;
    }
    if (autoReason.slug && autoReason.slug !== autoTemplateId) {
      return `${autoReason.reason ?? ""} Gewenst sjabloon '${autoReason.slug}' bestaat niet meer — teruggevallen op '${autoTemplateId}'.`.trim();
    }
    return `Geen installatietype-trefwoord of zakelijk klanttype herkend — teruggevallen op standaard sjabloon '${autoTemplateId}'.`;
  }, [autoReason, autoTemplateId]);



  const [templateId, setTemplateId] = useState<string>("standaard");
  const [templateTouched, setTemplateTouched] = useState(false);
  // Zolang de medewerker zelf nog geen chip heeft aangeklikt, volgt de selectie de auto-keuze.
  useEffect(() => {
    if (!templateTouched) setTemplateId(autoTemplateId);
  }, [autoTemplateId, templateTouched]);

  // Extra vangnet: als de handmatig gekozen `templateId` niet (meer) bestaat in
  // de sjablonenlijst (bv. admin archiveerde 'm terwijl dialog openstond), val
  // dan zichtbaar terug op de auto-keuze zodat er altijd iets vooringevuld is.
  const resolvedTemplateId = templates.some((t) => t.id === templateId) ? templateId : autoTemplateId;
  const defaultMessage =
    templates.find((t) => t.id === resolvedTemplateId)?.text ?? templates[0]?.text ?? "";


  const [waMessage, setWaMessage] = useState<string>("");
  // Houdt bij of de medewerker het bericht handmatig heeft aangepast; zolang
  // dat het geval is overschrijven we de tekst niet meer bij context-/template-
  // wijzigingen. Bij het klikken op een andere sjabloonchip wordt dit gereset
  // (zie chip-onClick verderop) zodat de nieuwe standaardtekst wél laadt.
  const [messageTouched, setMessageTouched] = useState(false);
  useEffect(() => {
    if (messageTouched) return;
    setWaMessage(defaultMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [c?.name, c?.orderNumber, c?.location, c?.startsAt, templateId, defaultMessage]);

  // Validatie: welke variabelen worden door dit sjabloon gebruikt, en zijn ze gevuld?
  // We kijken naar de ruwe body (bevat {tokens}). Voor fallback-templates
  // reconstrueren we tokens door bekende waarden weer te vervangen.
  const rawBody = useMemo(() => {
    const dbItem = ((tplQ.data?.items ?? []) as Array<{ slug: string; body: string }>).find(
      (t) => t.slug === templateId,
    );
    if (dbItem) return String(dbItem.body ?? "");
    const t = FALLBACK_TEMPLATES.find((x) => x.id === templateId)?.text ?? "";
    let out = t;
    if (firstName) out = out.split(firstName).join("{firstName}");
    if (c?.orderNumber) out = out.split(c.orderNumber).join("{orderNumber}");
    if (locationLabel) out = out.split(locationLabel).join("{location}");
    if (dateLabel) out = out.split(dateLabel).join("{date}");
    return out;
  }, [tplQ.data, templateId, FALLBACK_TEMPLATES, firstName, c?.orderNumber, locationLabel, dateLabel]);

  const requiredVars = useMemo(() => {
    const spec: Array<{ key: string; label: string; value: string; tokens: string[] }> = [
      { key: "firstName", label: "Naam", value: firstName, tokens: ["{firstName}", "{greeting}"] },
      { key: "orderNumber", label: "Order", value: c?.orderNumber ?? "", tokens: ["{orderNumber}", "{orderPart}"] },
      { key: "location", label: "Locatie", value: locationLabel, tokens: ["{location}", "{wherePart}"] },
      { key: "date", label: "Datum", value: dateLabel, tokens: ["{date}", "{whenPart}"] },
    ];
    return spec
      .filter((s) => s.tokens.some((t) => rawBody.includes(t)))
      .map((s) => ({ ...s, missing: !s.value.trim() }));
  }, [rawBody, firstName, c?.orderNumber, locationLabel, dateLabel]);

  const missingVars = requiredVars.filter((v) => v.missing);
  const unresolvedTokens = Array.from(new Set(waMessage.match(/\{[a-zA-Z]+\}/g) ?? []));
  const canSendWa =
    !noTemplatesConfigured &&
    missingVars.length === 0 &&
    unresolvedTokens.length === 0 &&
    !!waMessage.trim();

  const waUrl = phoneDigits && waMessage ? `https://wa.me/${phoneDigits}?text=${encodeURIComponent(waMessage)}` : null;

  const handleSubmit = async () => {
    setBusy(true);
    try {
      await complete({
        data: {
          eventId: props.event.id,
          satisfied: satisfied === "yes",
          completionNote: note.trim() || null,
        },
      });
      toast.success("Klus afgerond.");
      props.onDone();
    } catch (e: any) {
      toast.error(e?.message ?? "Afronden mislukt");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={props.onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="size-5 text-emerald-600" /> Klus afronden
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="rounded-md bg-slate-50 p-3">
            <div className="font-medium">{props.event.title}</div>
            <div className="text-xs text-slate-600">
              {new Date(props.event.starts_at).toLocaleString("nl-NL")}
            </div>
          </div>

          {contactQ.isLoading ? (
            <div className="flex items-center gap-2 text-slate-500">
              <Loader2 className="size-4 animate-spin" /> Contactgegevens laden…
            </div>
          ) : (
            <>
              <div className="rounded-md border p-3 text-xs space-y-1">
                <div><span className="text-slate-500">Klant:</span> {c?.name ?? "—"}</div>
                <div><span className="text-slate-500">E-mail:</span> {c?.email ?? <span className="text-red-600">onbekend</span>}</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span><span className="text-slate-500">Telefoon:</span> {c?.phone ?? <span className="text-red-600">onbekend</span>}</span>
                  {c?.phone && (
                    <a
                      href={`/admin/whatsapp?phone=${encodeURIComponent(c.phone)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 rounded border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-800 hover:bg-emerald-100"
                      title="Open WhatsApp-chat met deze klant"
                    >
                      💬 Open chat met deze klant
                    </a>
                  )}
                </div>
                <div><span className="text-slate-500">Ordernummer:</span> {c?.orderNumber ?? <span className="text-amber-700">geen order gekoppeld</span>}</div>
              </div>

              <div>
                <Label className="text-xs font-medium">Stap 1 — persoonlijk bericht naar klant</Label>
                <div className="mt-1 flex flex-wrap gap-1 text-[10px] text-slate-600">
                  <span className="rounded bg-slate-100 px-1.5 py-0.5">Naam: {firstName || "—"}</span>
                  <span className="rounded bg-slate-100 px-1.5 py-0.5">Locatie: {locationLabel || "—"}</span>
                  <span className="rounded bg-slate-100 px-1.5 py-0.5">Datum: {dateLabel || "—"}</span>
                  {c?.orderNumber && (
                    <span className="rounded bg-slate-100 px-1.5 py-0.5">Order: {c.orderNumber}</span>
                  )}
                </div>
                {noTemplatesConfigured && (
                  <div
                    role="alert"
                    className="mt-2 rounded-md border border-red-300 bg-red-50 p-2 text-[11px] text-red-800"
                  >
                    <div className="font-semibold">Geen WhatsApp-sjablonen beschikbaar</div>
                    <div className="mt-1">
                      Er staan geen actieve tevredenheids-sjablonen in de database. Verzenden is
                      geblokkeerd om te voorkomen dat een leeg bericht naar de klant gaat.
                    </div>
                    <div className="mt-1">
                      Vraag een beheerder om ten minste één sjabloon te activeren op{" "}
                      <a
                        href="/admin/whatsapp-templates"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium underline hover:no-underline"
                      >
                        /admin/whatsapp-templates
                      </a>
                      .
                    </div>
                  </div>
                )}
                <div className="mt-2">

                  <div className="flex items-center justify-between">
                    <Label className="text-[10px] uppercase tracking-wide text-slate-500">
                      Sjabloon (klant-/installatietype)
                    </Label>
                    {templateTouched && templateId !== autoTemplateId && (
                      <button
                        type="button"
                        onClick={() => {
                          setTemplateTouched(false);
                          setTemplateId(autoTemplateId);
                        }}
                        className="text-[10px] text-slate-500 underline hover:text-slate-700"
                      >
                        Auto-keuze herstellen
                      </button>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    {templates.map((t) => {
                      const isAuto = t.id === autoTemplateId;
                      const isActive = templateId === t.id;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => {
                            setTemplateTouched(true);
                            setTemplateId(t.id);
                            // Nieuw sjabloon → handmatige tekst-override loslaten
                            // zodat de nieuwe standaardtekst zichtbaar wordt.
                            setMessageTouched(false);
                          }}
                          title={isAuto ? `Auto: ${autoReasonText}` : undefined}
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] transition ${
                            isActive
                              ? "border-emerald-600 bg-emerald-50 text-emerald-800"
                              : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
                          }`}
                        >
                          {t.label}
                          {isAuto && (
                            <span className="rounded-sm bg-emerald-600/10 px-1 text-[9px] font-semibold uppercase tracking-wide text-emerald-700">
                              auto
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {/* Uitleg-regel onder de chips: welke regel heeft de auto-keuze veroorzaakt? */}
                  <div className="mt-1 flex items-start gap-1 text-[10px] leading-snug text-slate-500">
                    <span className="mt-[1px] inline-block rounded-sm bg-emerald-600/10 px-1 text-[9px] font-semibold uppercase tracking-wide text-emerald-700">
                      auto
                    </span>
                    <span>{autoReasonText}</span>
                  </div>
                  {/* Duidelijke samenvatting: welk sjabloon wordt straks verstuurd,
                      en of het een fallback / handmatige override is. */}
                  {(() => {
                    const chosenLabel =
                      templates.find((t) => t.id === resolvedTemplateId)?.label ?? resolvedTemplateId;
                    const isManual = templateTouched && resolvedTemplateId !== autoTemplateId;
                    const isFallback =
                      !isManual &&
                      (!autoReason.slug || autoReason.slug !== autoTemplateId);
                    const source = isManual ? "handmatig" : isFallback ? "fallback" : "auto";
                    const tone =
                      source === "handmatig"
                        ? "border-blue-300 bg-blue-50 text-blue-900"
                        : source === "fallback"
                          ? "border-amber-300 bg-amber-50 text-amber-900"
                          : "border-emerald-300 bg-emerald-50 text-emerald-900";
                    const badge =
                      source === "handmatig"
                        ? "bg-blue-600/15 text-blue-800"
                        : source === "fallback"
                          ? "bg-amber-600/15 text-amber-800"
                          : "bg-emerald-600/15 text-emerald-800";
                    return (
                      <div
                        className={`mt-2 flex flex-wrap items-center gap-2 rounded-md border px-2 py-1 text-[11px] ${tone}`}
                      >
                        <span className="text-[10px] uppercase tracking-wide opacity-70">
                          Wordt verzonden
                        </span>
                        <span className="font-semibold">{chosenLabel}</span>
                        <span
                          className={`rounded-sm px-1 text-[9px] font-semibold uppercase tracking-wide ${badge}`}
                          title={
                            source === "handmatig"
                              ? "Je hebt zelf een ander sjabloon gekozen dan de auto-selectie voorstelde."
                              : source === "fallback"
                                ? "Geen passende regel gevonden — teruggevallen op dit sjabloon."
                                : "Automatisch gekozen op basis van de regels."
                          }
                        >
                          {source}
                        </span>
                        <code className="rounded bg-black/5 px-1 py-[1px] text-[10px] opacity-70">
                          slug: {resolvedTemplateId}
                        </code>
                      </div>
                    );
                  })()}
                </div>


                <div className="mt-2">
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <div className="text-[10px] uppercase tracking-wide text-slate-500">
                      Bericht — bewerkbaar vóór versturen
                    </div>
                    {messageTouched && (
                      <span
                        className="rounded-sm bg-amber-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-amber-800"
                        title="Je hebt de standaardtekst van dit sjabloon aangepast."
                      >
                        handmatig bewerkt
                      </span>
                    )}
                  </div>
                  <textarea
                    value={waMessage}
                    onChange={(e) => {
                      setWaMessage(e.target.value);
                      if (!messageTouched) setMessageTouched(true);
                    }}
                    rows={5}
                    className={`w-full rounded-md border p-2 text-xs ${
                      unresolvedTokens.length > 0
                        ? "border-red-400 focus:border-red-500 focus:ring-red-300"
                        : "border-slate-300"
                    }`}
                    placeholder="Bericht aan klant…"
                    aria-invalid={unresolvedTokens.length > 0}
                    aria-describedby={unresolvedTokens.length > 0 ? "wa-placeholder-warning" : undefined}
                  />
                  {unresolvedTokens.length > 0 && (
                    <div
                      id="wa-placeholder-warning"
                      className="mt-1 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-900"
                    >
                      ⚠ Nog {unresolvedTokens.length} onopgeloste placeholder
                      {unresolvedTokens.length === 1 ? "" : "s"} in het bericht:{" "}
                      {unresolvedTokens.map((t, i) => (
                        <span key={t}>
                          {i > 0 && ", "}
                          <code className="rounded bg-amber-100 px-1">{t}</code>
                        </span>
                      ))}
                      . Vul deze in of vervang ze door tekst vóór verzenden.
                    </div>
                  )}
                </div>

                <div className="mt-2">
                  <div className="mb-1 text-[10px] uppercase tracking-wide text-slate-500">
                    Voorbeeld — zo ziet de klant het
                  </div>
                  <div
                    className="rounded-lg p-3"
                    style={{
                      backgroundImage:
                        "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.6) 0, transparent 40%), radial-gradient(circle at 80% 70%, rgba(255,255,255,0.5) 0, transparent 45%)",
                      backgroundColor: "#e5ddd5",
                    }}
                  >
                    <div className="flex justify-end">
                      <div className="relative max-w-[85%] rounded-lg bg-[#dcf8c6] px-3 py-2 text-[12px] leading-snug text-slate-900 shadow-sm">
                        <div className="mb-1 text-[10px] font-semibold text-emerald-800">
                          Elvion Group Nederland
                        </div>
                        <div className="whitespace-pre-wrap break-words">
                          {waMessage || <span className="italic text-slate-400">Bericht is leeg…</span>}
                        </div>
                        <div className="mt-1 text-right text-[9px] text-slate-500">
                          {new Date().toLocaleTimeString("nl-NL", { hour: "2-digit", minute: "2-digit" })} ✓✓
                        </div>
                      </div>
                    </div>
                    <div className="mt-1 text-right text-[10px] text-slate-600">
                      Naar: {c?.phone ?? "onbekend nummer"} · {waMessage.length} tekens
                    </div>
                  </div>
                </div>

                {c?.satisfactionPrompt && (
                  <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900 space-y-1">
                    <div>
                      ⚠ Er is al een tevredenheids-WhatsApp verstuurd voor deze klus op{" "}
                      <b>{new Date(c.satisfactionPrompt.sentAt).toLocaleString("nl-NL")}</b>.
                      Nogmaals versturen alleen als de klant er echt om vraagt.
                    </div>
                    {(c.satisfactionPrompt.templateSlug || c.satisfactionPrompt.autoTemplateSlug) && (
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className="opacity-75">Toen verstuurd sjabloon:</span>
                        <code className="rounded bg-amber-100 px-1 py-[1px] text-[10px]">
                          {c.satisfactionPrompt.templateSlug ?? "onbekend"}
                        </code>
                        {c.satisfactionPrompt.templateOverridden ? (
                          <span
                            className="rounded-sm bg-blue-600/15 px-1 text-[9px] font-semibold uppercase tracking-wide text-blue-800"
                            title={`Auto-selectie was '${c.satisfactionPrompt.autoTemplateSlug ?? "?"}' — handmatig gewijzigd.`}
                          >
                            handmatig
                          </span>
                        ) : c.satisfactionPrompt.autoTemplateSlug &&
                          c.satisfactionPrompt.autoTemplateSlug !== c.satisfactionPrompt.templateSlug ? (
                          <span
                            className="rounded-sm bg-amber-600/20 px-1 text-[9px] font-semibold uppercase tracking-wide text-amber-900"
                            title={`Auto-voorstel '${c.satisfactionPrompt.autoTemplateSlug}' was niet beschikbaar — fallback gebruikt.`}
                          >
                            fallback
                          </span>
                        ) : (
                          <span className="rounded-sm bg-emerald-600/15 px-1 text-[9px] font-semibold uppercase tracking-wide text-emerald-800">
                            auto
                          </span>
                        )}
                      </div>
                    )}
                    {c.satisfactionPrompt.fallbackReason && (
                      <details className="mt-1">
                        <summary className="cursor-pointer text-[10px] font-medium uppercase tracking-wide opacity-75 hover:opacity-100">
                          Waarom fallback? (details)
                        </summary>
                        <div className="mt-1 whitespace-pre-wrap break-words rounded bg-amber-100/60 px-2 py-1 text-[10px] leading-snug">
                          {c.satisfactionPrompt.fallbackReason}
                        </div>
                      </details>
                    )}
                  </div>
                )}
                {(missingVars.length > 0 || unresolvedTokens.length > 0) && (
                  <div className="mt-2 rounded-md border border-red-300 bg-red-50 p-2 text-[11px] text-red-800">
                    <div className="font-semibold">Kan bericht nog niet versturen:</div>
                    <ul className="mt-1 list-disc pl-4 space-y-0.5">
                      {missingVars.map((v) => (
                        <li key={v.key}>
                          Variabele <b>{v.label}</b> ontbreekt — vul dit veld eerst aan bij de klus/klant.
                        </li>
                      ))}
                      {unresolvedTokens.map((t) => (
                        <li key={t}>Onbekende placeholder <code className="rounded bg-red-100 px-1">{t}</code> in het bericht.</li>
                      ))}
                    </ul>
                  </div>
                )}
                <div className="mt-2 flex items-center gap-2">
                  {waUrl ? (
                    <button
                      type="button"
                      disabled={!canSendWa}
                      onClick={async () => {
                        if (!canSendWa) return;
                        const already = !!c?.satisfactionPrompt;
                        if (already) {
                          const ok = window.confirm(
                            "Er is al een tevredenheidsvraag verstuurd voor deze klus. Toch nogmaals versturen?",
                          );
                          if (!ok) return;
                        }
                        try {
                          // Bereken of we een fallback gebruiken en zo ja: waarom.
                          // Dit wordt server-side gelogd + opgeslagen in
                          // whatsapp_satisfaction_prompts.fallback_reason
                          // zodat de oorzaak later terug te vinden is.
                          const isManualOverride =
                            templateTouched && resolvedTemplateId !== autoTemplateId;
                          const usedFallback =
                            !isManualOverride &&
                            (!autoReason.slug || autoReason.slug !== resolvedTemplateId);
                          let fallbackReason: string | null = null;
                          if (usedFallback) {
                            // Verzamel welke keyword-patronen zijn geprobeerd
                            // (uit de actieve auto-regels) en welke haystack
                            // (klus/type/locatie) niet matchte.
                            const haystack = [
                              c?.jobTitle,
                              (c as any)?.eventType,
                              c?.location,
                            ]
                              .filter(Boolean)
                              .join(" ")
                              .trim();
                            const triedKeywords = autoRules
                              .filter((r) => !!r.keywordPattern)
                              .map((r) => r.keywordPattern as string);
                            const parts: string[] = [];
                            if (!autoReason.slug) {
                              parts.push(
                                `Geen auto-regel matchte (klanttype='${(c as any)?.customerType ?? "onbekend"}', klus/locatie='${haystack || "leeg"}').`,
                              );
                            } else if (autoReason.slug !== resolvedTemplateId) {
                              parts.push(
                                `Auto-voorstel '${autoReason.slug}' was niet beschikbaar in de sjablonenlijst — teruggevallen op '${resolvedTemplateId}'.`,
                              );
                            }
                            if (triedKeywords.length > 0) {
                              parts.push(
                                `Geprobeerde installatietype-trefwoorden: ${triedKeywords.join(" | ")}.`,
                              );
                            }
                            fallbackReason = parts.join(" ").slice(0, 2000);
                          }
                          const res: any = await recordPrompt({
                            data: {
                              eventId: props.event.id,
                              force: already,
                              body: waMessage,
                              templateSlug: resolvedTemplateId,
                              autoTemplateSlug: autoTemplateId,
                              templateOverridden: isManualOverride,
                              fallbackReason,
                            },
                          });

                          if (res?.ok === false) {
                            toast.warning("Al eerder verstuurd — geen nieuwe verzending gelogd.");
                            return;
                          }
                          await contactQ.refetch();
                          if (res?.sendVia === "cloud" && res?.waMessageId) {
                            toast.success(
                              res.resent
                                ? `WhatsApp opnieuw verstuurd via Cloud API (id ${String(res.waMessageId).slice(-6)}).`
                                : `WhatsApp verstuurd via Cloud API (id ${String(res.waMessageId).slice(-6)}).`,
                            );
                          } else {
                            if (res?.error) {
                              toast.warning(`Cloud API mislukte (${res.error}). WhatsApp Web wordt geopend als fallback.`);
                            } else {
                              toast.info("Geen Cloud API geconfigureerd — WhatsApp Web geopend.");
                            }
                            if (waUrl) window.open(waUrl, "_blank", "noopener,noreferrer");
                          }
                        } catch (e: any) {
                          toast.error(e?.message ?? "Kon verzending niet loggen");
                        }
                      }}
                      className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-emerald-600"
                    >
                      <MessageCircle className="size-3.5" />
                      {c?.satisfactionPrompt ? "WhatsApp opnieuw versturen" : "WhatsApp versturen"}
                    </button>

                  ) : (
                    <div className="text-xs text-amber-700">Geen telefoonnummer — vraag de klant persoonlijk of bel na.</div>
                  )}
                  <button
                    type="button"
                    disabled={!waMessage.trim()}
                    onClick={() => {
                      const remembered =
                        typeof window !== "undefined"
                          ? window.localStorage.getItem("wa-test-phone") ?? ""
                          : "";
                      const input = window.prompt(
                        "Test-telefoonnummer (met landcode, bijv. 316xxxxxxxx). Er wordt niets gelogd, alleen een WhatsApp-venster geopend.",
                        remembered,
                      );
                      if (!input) return;
                      const digits = input.replace(/[^\d]/g, "");
                      if (digits.length < 8) {
                        toast.error("Ongeldig telefoonnummer");
                        return;
                      }
                      try { window.localStorage.setItem("wa-test-phone", digits); } catch {}
                      const url = `https://wa.me/${digits}?text=${encodeURIComponent(
                        `[TEST — voorbeeld voor klus ${props.event.title}]\n\n${waMessage}`,
                      )}`;
                      window.open(url, "_blank", "noopener,noreferrer");
                      toast.info("Testbericht geopend — niet gelogd als klant-uitnodiging.");
                    }}
                    className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-[11px] text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                    title="Stuur een voorbeeld naar een eigen test-nummer, zonder logging"
                  >
                    <MessageCircle className="size-3.5" /> Voorbeeld naar mij
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setMessageTouched(false);
                      setWaMessage(defaultMessage);
                    }}
                    disabled={!messageTouched && waMessage === defaultMessage}
                    className="text-[11px] text-slate-500 underline hover:text-slate-700 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-50"
                  >
                    Reset naar standaardtekst
                  </button>
                </div>


              </div>


              <div>
                <Label className="text-xs font-medium">Stap 2 — reactie klant</Label>
                <div className="mt-1 flex gap-2">
                  {[
                    { v: "yes", label: "Tevreden" },
                    { v: "no", label: "Niet tevreden" },
                    { v: "unknown", label: "Nog geen reactie" },
                  ].map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => setSatisfied(o.v as any)}
                      className={`rounded-md border px-3 py-1.5 text-xs ${
                        satisfied === o.v
                          ? o.v === "yes"
                            ? "border-emerald-600 bg-emerald-50 text-emerald-800"
                            : o.v === "no"
                              ? "border-red-500 bg-red-50 text-red-800"
                              : "border-slate-400 bg-slate-100 text-slate-800"
                          : "border-slate-200 hover:bg-slate-50"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label htmlFor="note" className="text-xs font-medium">Interne notitie (optioneel)</Label>
                <Textarea
                  id="note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Bijv. klein detail nog na te leveren, feedback van klant…"
                  className="mt-1 text-xs"
                  rows={2}
                />
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={props.onClose} disabled={busy}>Annuleren</Button>
          <Button onClick={handleSubmit} disabled={busy || satisfied === "unknown"}>
            {busy && <Loader2 className="mr-1 size-3.5 animate-spin" />}
            Klus afronden
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
